import { z } from 'zod'

export const nullableString = z.string().nullable()
export const sha256String = z.string().regex(/^[a-f0-9]{64}$/)

export const nnppSelectedSpecificationSchema = z
  .object({
    source_id: z.string().min(1),
    extraction_key: sha256String,
  })
  .strict()

export const nnppMatchingRunSchema = z
  .object({
    run_id: z.string().min(1),
    status: z.enum([
      'waiting_for_linking',
      'queued',
      'running',
      'blocked',
      'failed',
      'completed',
    ]),
    selected_specifications: z.array(nnppSelectedSpecificationSchema),
    participating_documents: z
      .array(
        z
          .object({
            document_id: z.string().min(1),
            extraction_key: sha256String,
          })
          .strict()
      )
      .default([]),
    error_message: nullableString,
    completed_at: z.string().datetime({ offset: true }).nullable(),
    retry_available_at: z
      .string()
      .datetime({ offset: true })
      .nullable()
      .default(null),
  })
  .strict()
  .nullable()

const reviewSourceEvidenceSchema = z.object({
  pdf_page_number: z.number().int().min(1).nullable().default(null),
  printed_page_label: nullableString.default(null),
  section: z.array(z.string()),
  evidence_text: nullableString,
  evidence_min_ocr_confidence: z
    .number()
    .min(0)
    .max(1)
    .nullable()
    .default(null),
  bbox: z.array(z.number()).length(4).nullable(),
})

export const characteristicPayloadSchema = z.object({
  llm_field_label: nullableString,
  llm_value: nullableString,
  unit: nullableString,
})

export const characteristicReviewSchema = z.object({
  target_kind: z.literal('characteristic'),
  extraction_key: sha256String,
  extraction_run_id: z.string().regex(/^\d{8}T\d{12}Z-[a-z0-9]{6}$/),
  target_row_id: z.string(),
  original_payload_sha256: sha256String,
  original_review_reasons: z.array(z.string()),
  resolved_review_reasons: z.array(z.string()),
  pending_review_reasons: z.array(z.string()),
  human_reviewed: z.boolean(),
  effective_human_confidence: z.number().nullable(),
  reviewer_user_id: nullableString,
  reviewed_at: nullableString,
  source_evidence: reviewSourceEvidenceSchema,
  original_payload: characteristicPayloadSchema,
  effective_payload: characteristicPayloadSchema,
  read_only_context: z.object({
    raw_field_label: nullableString,
    raw_value: nullableString,
    record_type: nullableString,
    record_id: nullableString,
    test_type: nullableString,
    test_context: nullableString,
    characteristic_index: z.number().int().nullable(),
    source_table_id: nullableString,
    source_table_title: nullableString.default(null),
    source_table_section_title: nullableString.optional(),
    source_table_kind: nullableString.optional(),
    field_role: z.enum(['value', 'option']).optional(),
    selection_state: z
      .enum(['selected', 'unselected', 'ambiguous'])
      .nullable()
      .optional(),
    asserted: z.boolean().optional(),
    source_record_id: nullableString,
    source_cell_index: z.number().int().nullable(),
  }),
})

export type NnppCharacteristicReview = z.infer<
  typeof characteristicReviewSchema
>
export type NnppMatchingRun = z.infer<typeof nnppMatchingRunSchema>
export type NnppSelectedSpecification = z.infer<
  typeof nnppSelectedSpecificationSchema
>
